/**
 * Audio toggle — wires a DOM element to the audio engine's mute state.
 *
 * The parent (game.ts / index.html) owns the actual button markup and
 * styling; this module only:
 *
 *  - Reflects the engine's current mute state onto the button (label,
 *    aria-pressed, data-muted attribute for CSS hooks).
 *  - Unlocks the engine on first click (browsers require a user gesture)
 *    and toggles the mute state thereafter.
 *  - Starts the ambient bed when the engine first unlocks.
 *
 * The button is expected to contain a single text node we can overwrite,
 * or a child with `[data-audio-label]` if richer markup is needed.
 */

import { engine } from '../audio/engine';
import { startAmbient } from '../audio/ambient';

const LABEL_MUTED = 'Sound off';
const LABEL_ON = 'Sound on';

export function installAudioToggle(button: HTMLElement): void {
  // Initial paint reflects whatever localStorage said.
  paint(button);

  button.addEventListener('click', () => {
    void handleClick(button);
  });
}

async function handleClick(button: HTMLElement): Promise<void> {
  const wasUnlocked = engine.isUnlocked();
  if (!wasUnlocked) {
    await engine.unlock();
    // First-time unlock: only start ambient if we're not muted.
    if (!engine.isMuted()) {
      startAmbient();
    }
  } else {
    // Toggle the mute state.
    engine.setMuted(!engine.isMuted());
  }
  paint(button);
}

function paint(button: HTMLElement): void {
  const muted = engine.isMuted();
  const label = muted ? LABEL_MUTED : LABEL_ON;

  button.setAttribute('aria-pressed', muted ? 'true' : 'false');
  button.setAttribute('aria-label', label);
  button.dataset.muted = muted ? 'true' : 'false';

  // Update the inner text without nuking child markup the parent may have added.
  const slot = button.querySelector('[data-audio-label]');
  if (slot) {
    slot.textContent = label;
  } else if (button.children.length === 0) {
    button.textContent = label;
  }
  // If the button has children but no labelled slot, leave its DOM alone
  // — the parent provided custom markup and only the aria/data attrs matter.
}
